<template>
  <section class="company-tabs">
    <nav ref="navScroller" class="company-tabs__nav">
      <ul :id="resolvedNavId"
          ref="navList"
      >
        <li v-for="(tab, index) in tabs"
            :key="tab.key || tab.icon || 'tab_' + index"
        >
          <a href="javascript:void(0)" @click.prevent.stop="onTabClick(index)">
            <i v-if="tab.icon" :class="tab.icon" aria-hidden="true"></i>
            <span>{{ tab.label }}</span>
          </a>
        </li>
      </ul>
    </nav>

    <ul :id="resolvedContentId"
        class="uk-switcher company-tabs__content"
    >
      <slot/>
    </ul>
  </section>
</template>

<script>
let companyTabsInstance = 0;

export default {
  name: "CompanyTabs",
  props: {
    tabs: {
      type: Array,
      default: () => [],
    },
    navId: {
      type: String,
      default: null,
    },
    contentId: {
      type: String,
      default: null,
    },
    animation: {
      type: String,
      default: "uk-animation-slide-right-medium, uk-animation-slide-left-medium",
    },
  },
  data() {
    companyTabsInstance += 1;
    return {
      instanceId: `company-tabs-${companyTabsInstance}`,
      switcherInstance: null,
    };
  },
  computed: {
    resolvedNavId() {
      return this.navId || `${this.instanceId}-nav`;
    },
    resolvedContentId() {
      return this.contentId || `${this.instanceId}-content`;
    },
  },
  watch: {
    // Switcher, nav <li>'lerini icerik <li>'lerine init aninda index ile
    // esliyor. Sekme sayisi sonradan degisirse (ornek: SurveyAddEdit'te
    // edit -> add gecisinde Excel sekmesi eklenir) eski eslesme gecersiz
    // kaliyor, yeni sekme tiklanamiyor.
    tabs(newTabs, oldTabs) {
      if ((newTabs || []).length === (oldTabs || []).length) return;

      this.$nextTick(this.initSwitcher);
    },
  },
  mounted() {
    this.$nextTick(this.initSwitcher);
  },
  beforeDestroy() {
    this.destroySwitcher();
  },
  methods: {
    getUIkit() {
      return window.UIkit;
    },
    initSwitcher() {
      const UIkit = this.getUIkit();
      const nav = this.$refs.navList;
      if (!UIkit || !nav) return;

      this.destroySwitcher();
      this.switcherInstance = UIkit.switcher(nav, {
        connect: `#${this.resolvedContentId}`,
        animation: this.animation,
      });

      if (UIkit.util && typeof UIkit.util.on === "function") {
        UIkit.util.on(nav, "shown", this.onTabShown);
      }

      this.centerActiveTab();
    },
    destroySwitcher() {
      const UIkit = this.getUIkit();
      const nav = this.$refs.navList;

      if (nav && UIkit && UIkit.util && typeof UIkit.util.off === "function") {
        UIkit.util.off(nav, "shown", this.onTabShown);
      }

      if (this.switcherInstance && typeof this.switcherInstance.$destroy === "function") {
        this.switcherInstance.$destroy(true);
      }

      this.switcherInstance = null;
    },
    onTabClick(index) {
      if (this.switcherInstance) {
        this.switcherInstance.show(index);
      }
      this.$nextTick(this.centerActiveTab);
    },
    onTabShown() {
      this.$nextTick(this.centerActiveTab);
    },
    centerActiveTab() {
      if (typeof window === "undefined" || !window.matchMedia("(max-width: 767px)").matches) {
        return;
      }

      const scroller = this.$refs.navScroller;
      const activeTab = scroller && scroller.querySelector("li.uk-active");
      if (!scroller || !activeTab) return;

      const scrollerRect = scroller.getBoundingClientRect();
      const activeRect = activeTab.getBoundingClientRect();
      const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const targetScrollLeft = Math.min(
          maxScrollLeft,
          Math.max(
              0,
              scroller.scrollLeft +
              activeRect.left -
              scrollerRect.left -
              (scroller.clientWidth - activeRect.width) / 2
          )
      );
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (typeof scroller.scrollTo === "function") {
        scroller.scrollTo({
          left: targetScrollLeft,
          behavior: reduceMotion ? "auto" : "smooth",
        });
      } else {
        scroller.scrollLeft = targetScrollLeft;
      }
    },
  },
};
</script>

<style scoped>
/* Sekme seridi duz akar: nav'in kendi zemini/padding'i yok, aktif sekme
   kutu degil alt cizgi ile isaretleniyor. Ilk sekme en soldan baslar. */
.company-tabs {
  min-width: 0;
  overflow: visible;
}

.company-tabs__nav {
  overflow: hidden;
  padding: 0;
  border-bottom: 1px solid var(--education-border, #e2e8f0);
  background: none;
}

.company-tabs__nav ul {
  display: flex;
  gap: 28px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.company-tabs__nav li a {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  /* nav'in 1px alt cizgisini ortmesi icin */
  margin-bottom: -1px;
  padding: 10px 0;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--education-muted, #64748b);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.company-tabs__nav li a i {
  flex: 0 0 auto;
  font-size: 18px;
}

.company-tabs__nav li a:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.25);
  outline-offset: 2px;
}

/* Marka rengi :root'a `--primaryColor` olarak tenant ayarindan yaziliyor */
.company-tabs__nav li.uk-active a {
  border-bottom-color: var(--primaryColor, #3F51B5);
  color: var(--primaryColor, #3F51B5);
  font-weight: 600;
}

.company-tabs__nav li.uk-active a i {
  color: inherit;
}

.company-tabs__content {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.company-tabs__content > li {
  min-width: 0;
  padding: 24px 0 0;
}

.company-tabs__content > li > h4,
.company-tabs__content ::v-deep .comments > h4 {
  margin-top: 0;
  color: var(--education-text, #1e293b);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
}

@media (max-width: 767px) {
  .company-tabs__nav {
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .company-tabs__nav::-webkit-scrollbar {
    display: none;
  }

  .company-tabs__nav ul {
    width: max-content;
    min-width: 100%;
    flex-wrap: nowrap;
    gap: 20px;
  }

  .company-tabs__nav li {
    flex: 0 0 auto;
  }

  .company-tabs__nav li a {
    min-height: 48px;
    white-space: nowrap;
  }

  .company-tabs__content > li {
    padding: 16px 0 0;
  }

  .company-tabs__content > li > h4,
  .company-tabs__content ::v-deep .comments > h4 {
    font-size: 18px;
  }
}

@media (max-width: 479px) {
  .company-tabs__nav ul {
    gap: 16px;
  }

  .company-tabs__nav li a {
    gap: 5px;
    font-size: 13px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .company-tabs__nav li:not(.uk-active) a:hover {
    color: #334155;
  }
}

@media (prefers-reduced-motion: reduce) {
  .company-tabs__nav li a {
    transition: none;
  }
}
</style>
